'use client';

import { datadogLogs, Logger as DataDogLogger, LogsEvent } from '@datadog/browser-logs';
import { datadogRum, RumEvent } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { isbot } from 'isbot';
import { createContext, ReactNode, useEffect, useState } from 'react';

import { stripNulls } from '@/helpers/formatters';
import { reloadPage } from '@/helpers/reloadPage';
import { scrubEmbedTokenFromUrl } from '@/helpers/url';
import { AffiliateId, VisitorCookie } from '@/models';

import { normalizeExperiments } from './normalizers';
import { redactPii } from './redactPii';
import { useFunnelType } from './useFunnelType';
import { useScrollDepth } from './useScrollDepth';

const clientToken = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN ?? '';
const applicationId = process.env.NEXT_PUBLIC_DD_APP_ID ?? '';
const NODE_ENV = process.env.NODE_ENV || 'development';
const envName = process.env.NEXT_PUBLIC_DD_ENV || 'development';
const version = process.env.NEXT_PUBLIC_VERSION || '0.0.0';
const datadogUrl = 'datadoghq.com';
const service = 'consumer-marketplace';

// I believe, most of these errors are not actionable and are caused either by browser privacy plugins or the user
// navigating away before the JavaScript can finish. If any of these are actionable, please fix them.
const ignoreErrors: RegExp[] = [
  /^Error invoking .+: Java /, // Android in-app browser WebView bridge errors (postMessage, enableDidUserTypeOnKeyboardLogging, etc.)
  /^null is not an object \(evaluating '[a-z]{1,2}\.id'\)/, // Minified third-party code error, likely Segment or DataDog SDK race condition (variable letter varies by minifier)
  /^.*Error\.captureStackTrace is not a function/, // V8-specific API not available in Safari/WebKit, third-party library issue
  // Keeping Vercel/Cloudflare errors visible to monitor hosting reliability
  // /^BadGatewayError:/, // Vercel edge-to-origin connection failures
  // /cf-error-details/, // Cloudflare error page HTML in response body
  // /ROUTER_EXTERNAL_TARGET_ERROR/, // Vercel edge-to-origin connection failures
  /^blocked by 'font-src' directive/,
  /^Can't find variable: _AutofillCallbackHandler/,
  /^Error eagerly loading saved frequency rules/, // HubSpot web-interactives script logging IndexedDB failures (insecure context/privacy mode), handled by HubSpot
  /^Error sending segment performance metrics TypeError/,
  /^Connection closed\.$/, // RSC streaming connection closed when user navigates away
  /^Failed to execute 'enqueue' on 'ReadableStreamDefaultController'/, // Browser extension or bot interfering with Next.js RSC streaming
  /^Failed to fetch RSC payload/,
  /^Failed to read the 'sessionStorage' property from 'Window'/,
  /^Failed to create PluginFactory/, // Segment plugin loading failures, not actionable
  /^ga is not defined$/, // Legacy Universal Analytics tag configured in the GTM container; analytics.js (which defines window.ga) is never loaded on this site
  /^Fetch error GET https:\/\/(api|cdn)\.segment/,
  /^Fetch error GET https:\/\/www\.facebook/,
  /^Fetch error POST https:\/\/(api|cdn)\.segment/,
  /^Fetch error POST https:\/\/www\.google/,
  /^HandlingStack: [\s\S]+node_modules_%40segment_analytics/,
  /^HandlingStack: [\s\S]+cdn\.trustedform\.com\/bootstrap\.js/, // TrustedForm logging a rejected DOMException; message itself is just "[DOMException]"
  /indexedDB cache error/, // IndexedDB errors from privacy mode or storage limits, not actionable
  /^Launcher Failed\.$/, // Most likely a third-party error
  /^(TypeError: ){0,2}Load failed/, // Safari generic error for resource loading failures
  /^(ChunkLoadError|Failed to load chunk|Loading chunk \d+ failed|Importing a module script failed\.)/, // Next.js/Turbopack stale chunk failures during deployments; auto-reload handles recovery (reactPlugin bypasses recoverFromChunkError); "Importing a module script failed." is the Safari/WebKit equivalent
  /Remote Plugin ChunkLoadError/, // Segment third-party plugin chunk failures (cdn.segment.com), not our assets
  /^Minified React error #418/, // Hydration mismatch - typically from bot crawlers
  /^MktoForms2 is not defined/, // Marketo form library referenced by a GTM Custom HTML tag; we never load it
  /^NetworkError when attempting to fetch resource\./,
  /^(NotFoundError: )?The object can not be found here\./, // React DOM removeChild timing issue during navigation; RUM usually sends the bare message
  /^PLACES_AUTOCOMPLETE: UNKNOWN_ERROR/, // Google Places API server error, not actionable
  /^PLACES_GET_PLACE: UNAVAILABLE/, // Google Places API (fetchFields) transient outage, not actionable
  /^(Uncaught ")?(Script error\.)"?$/, // CORS error with no actionable info, common in third-party scripts and in-app browsers
  /^Uncaught "Empty reason"$/, // Empty Promise rejection from third-party scripts or browser lifecycle events
  /^Could not load "(map|marker|onion)"\./, // Google Maps API internal module loading failure, transient network/resource issue
  /^The Google Maps JavaScript API could not load/,
  /^The map is initialized without a valid Map ID/, // Google Maps Advanced Markers warning, not actionable
  /^Google Maps JavaScript API error: NotLoadingAPIFromGoogleMapsError/, // Browser extension or proxy intercepting Google Maps script
  /^Google Maps JavaScript API: Unable to fetch configuration for mapId/, // Google Maps config fetch error, not actionable
  /^TrustedForm Script failed to load/,
  /^csp_violation: 'trusted-types-sink' blocked by 'require-trusted-types-for' directive/, // TrustedForm violates Google Translate's Trusted Types CSP — not our policy, no user impact
  /^(TypeError: )?Failed to fetch/, // Transient network errors, user navigation, connectivity issues
  /^(TypeError: )?network error$/, // Chrome alternate form of "Failed to fetch" network-level error
  /^Uncaught "Object Not Found Matching Id/, // likely CefSharp bot crawlers (eg Microsoft Outlook SafeLinks)
  /^undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
  /^XHR error GET https:\/\/maps\.googleapis/,
  /^XHR error POST .*\/cdn-cgi\/rum\?/,
  /^XHR error POST https:\/\/events\.launchdarkly/,
  /^Zotero Connector:/, // Zotero citation manager browser extension errors, not actionable
];

// Errors from third-party scripts injected into the page (e.g., Google Translate proxy)
const ignoreErrorStacks: RegExp[] = [
  /chrome-extension:\/\//, // Browser extension errors (e.g., Talisman wallet, ad blockers), not actionable
  /file:\/\/\/Applications\/[\s\S]*\.appex\//, // Safari app-extension errors (e.g. PayPal Honey), not actionable
  /postMessage @ \[native code\]/, // Facebook in-app browser postMessage restriction errors, not actionable
  /translate\.goog/, // Google Translate proxy errors when users view site via translate.goog
  /translate\.googleapis\.com/, // Google Translate API errors
];

export type AddError = (error: unknown, context?: object) => void;
export type Logger = Pick<DataDogLogger, 'info' | 'warn'>;

type DataDogContextType = {
  addAction: (action: string, context?: object) => void;
  addError: AddError;
  logger: Logger;
  setUserProperty: (key: string, property: unknown) => void;
};

const CHUNK_RELOAD_KEY = 'chunk-reload-at';
const CHUNK_RELOAD_WINDOW_MS = 10_000;

// Reload once after a deploy when our JS is stale; returns true so callers skip DataDog.
export const recoverFromChunkError = (error: unknown) => {
  const message = (error as Error | undefined)?.message ?? '';

  if (
    /Remote Plugin/.test(message) ||
    !/ChunkLoadError|Loading chunk \d+ failed|Failed to load chunk|Importing a module script failed/.test(message)
  ) {
    return false;
  }

  const now = Date.now();
  if (now - (Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0) >= CHUNK_RELOAD_WINDOW_MS) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    reloadPage();
  }

  return true;
};

// exported for testing
export const contextDefault: DataDogContextType = {
  addAction: (...args) => datadogRum.addAction(...args),
  addError: (error, context) => {
    if (recoverFromChunkError(error)) {
      return;
    }

    // eslint-disable-next-line no-console
    console.error(error, context ? redactPii(context) : undefined);
  },
  logger: {
    // eslint-disable-next-line no-console
    info: (message, messageContext, error) => console.info(message, redactPii(messageContext), error),
    // eslint-disable-next-line no-console
    warn: (message, messageContext, error) => console.warn(message, redactPii(messageContext), error),
  },
  setUserProperty: (...args) => datadogRum.setUserProperty(...args),
};

export const DataDogContext = createContext<DataDogContextType>(contextDefault);

const reportVisibilityChange = () => {
  datadogRum.addAction('visibilityChange', {
    page: window.location.pathname,
    state: document.visibilityState,
  });
};

type Props = { children: ReactNode; sessionPersistence?: 'memory'; visitor: VisitorCookie | undefined };

export const DataDogProvider = ({ children, sessionPersistence, visitor }: Props) => {
  const funnel = useFunnelType();
  const [logger, setLogger] = useState<DataDogContextType['logger']>(contextDefault.logger);
  const [addError, setAddError] = useState<DataDogContextType['addError']>(() => contextDefault.addError);

  useScrollDepth();

  useEffect(() => {
    document.addEventListener('visibilitychange', reportVisibilityChange, { passive: true });

    const onRejection = (e: PromiseRejectionEvent) => recoverFromChunkError(e.reason);
    const onError = (e: ErrorEvent) => recoverFromChunkError(e.error ?? e);
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('error', onError);

    return () => {
      document.removeEventListener('visibilitychange', reportVisibilityChange);
      window.removeEventListener('unhandledrejection', onRejection);
      window.removeEventListener('error', onError);
    };
  }, []);

  useEffect(() => {
    const config = datadogRum.getInitConfiguration();

    if (
      config ||
      NODE_ENV === 'development' ||
      !['staging', 'production'].includes(envName) ||
      !applicationId ||
      !clientToken ||
      window.E2E_TEST ||
      // Crawlers were most of our RUM errors (Bytespider alone ~60%) and their sessions aren't useful
      isbot(navigator.userAgent)
    ) {
      return;
    }

    datadogRum.setGlobalContextProperty('funnel.type', funnel.funnelType);
    datadogRum.setGlobalContextProperty('funnel.version', funnel.version);
    datadogLogs.init({
      beforeSend: logsBeforeSend,
      clientToken,
      env: envName,
      forwardConsoleLogs: ['error', 'info', 'warn'],
      service,
      sessionPersistence,
      sessionSampleRate: 100,
      site: datadogUrl,
      telemetrySampleRate: 0,
      // TODO: toogle 'not-granted' / 'granted' based on country for GRPR
      trackingConsent: 'granted',
      version,
    });

    setLogger({
      info: (message, messageContext, error) => datadogLogs.logger.info(message, redactPii(messageContext), error),
      warn: (message, messageContext, error) => datadogLogs.logger.warn(message, redactPii(messageContext), error),
    });
    setAddError(() => (error: unknown, context?: object) => {
      if (recoverFromChunkError(error)) {
        return;
      }

      datadogRum.addError(error, context ? redactPii(context) : undefined);
    });

    datadogRum.setGlobalContextProperty('funnel.type', funnel.funnelType);
    datadogRum.setGlobalContextProperty('funnel.version', funnel.version);
    datadogRum.init({
      // Specify URLs to propagate trace headers for connection between RUM and backend trace
      allowedTracingUrls: [/\/(api|google)\/v1\//],
      applicationId,
      beforeSend: rumBeforeSend,
      clientToken,
      defaultPrivacyLevel: 'mask-user-input',
      env: envName,
      plugins: [reactPlugin({ router: false })],
      service,
      sessionPersistence,
      sessionReplaySampleRate: 100,
      sessionSampleRate: 100,
      site: datadogUrl,
      telemetrySampleRate: 0,
      // TODO: toogle 'not-granted' / 'granted' based on country for GRPR
      trackingConsent: 'granted',
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      version,
    });

    datadogRum.setGlobalContextProperty('initialVisibility', document.visibilityState);

    if (visitor) {
      const { meta_values = {}, vid } = visitor;
      const {
        affiliate_id,
        channel,
        embed_in_service_area,
        embed_partner_id,
        experiments,
        landing_page,
        utm_campaign,
        utm_content,
        utm_medium,
        utm_source,
        utm_term,
      } = meta_values;
      const [affiliate_name] = Object.entries(AffiliateId).find(([, value]) => value === affiliate_id) ?? [];
      const isTester = affiliate_id === AffiliateId.TEST || utm_source === 'test';

      if (landing_page) datadogRum.setGlobalContextProperty('landingPage', landing_page);
      if (channel) datadogRum.setGlobalContextProperty('channel', channel);
      if (embed_partner_id) datadogRum.setGlobalContextProperty('embed.partner_id', embed_partner_id);
      if (typeof embed_in_service_area === 'boolean') {
        datadogRum.setGlobalContextProperty('embed.in_service_area', embed_in_service_area);
      }

      datadogRum.setUser(
        stripNulls({
          affiliate_id,
          affiliate_name,
          channel,
          experiments: normalizeExperiments(experiments),
          id: vid,
          isTester,
          utm_campaign,
          utm_content,
          utm_medium,
          utm_source,
          utm_term,
        })
      );
    }
  }, [funnel, sessionPersistence, visitor]);

  return <DataDogContext.Provider value={{ ...contextDefault, addError, logger }}>{children}</DataDogContext.Provider>;
};

// RUM prefixes error.message with the constructor name ("TypeError: foo"); ^-anchored
// patterns above are written against the bare message, so strip it before matching.
const stripErrorTypePrefix = (message: string) => message.replace(/^[A-Za-z]*Error: /, '');

const isIgnoredMessage = (message: string) =>
  ignoreErrors.some((ignore) => ignore.test(message) || ignore.test(stripErrorTypePrefix(message)));

// Exported for testing
export const logsBeforeSend = (event: LogsEvent) => {
  const { message, status } = event;
  if (status === 'error' && isIgnoredMessage(message)) {
    return false;
  }

  scrubEmbedTokenFromDdEvent(event);
  return true;
};

// Exported for testing
export const rumBeforeSend = (event: RumEvent) => {
  const { error, resource, type } = event;

  if (type === 'error' && error) {
    if (isIgnoredMessage(error.message) || ignoreErrors.some((ignore) => ignore.test(error.handling_stack ?? ''))) {
      return false;
    }

    if (ignoreErrorStacks.some((ignore) => ignore.test(error.stack ?? ''))) {
      return false;
    }
  }

  // Ignore aborted fetch requests (status code 0)
  if (type === 'resource' && resource) {
    if (['fetch', 'xhr'].includes(resource.type) && resource.status_code === 0) {
      return false;
    }
  }

  scrubEmbedTokenFromDdEvent(event);
  return true;
};

const scrubEmbedTokenFromDdEvent = (event: {
  http?: { url?: string };
  resource?: { url?: string };
  view?: { referrer?: string; url?: string };
}) => {
  if (event.view?.url) {
    event.view.url = scrubEmbedTokenFromUrl(event.view.url);
  }
  if (event.view?.referrer) {
    event.view.referrer = scrubEmbedTokenFromUrl(event.view.referrer);
  }
  if (event.resource?.url) {
    event.resource.url = scrubEmbedTokenFromUrl(event.resource.url);
  }
  if (event.http?.url) {
    event.http.url = scrubEmbedTokenFromUrl(event.http.url);
  }
};
